// Вкладка «Тарифы»: устройство страницы /pricing и весь её текст.
//
// Язык переключается тумблером наверху — раньше под это была отдельная вкладка
// «Переводы» с тем же документом, развёрнутым по строкам «оригинал → перевод».
// Две вкладки на один документ приходилось держать в голове разом: правишь
// цену — половина полей всё равно ждёт перевода на соседнем экране. Тумблер
// решает то же самое одним движением: поля те же, меняется только язык, а
// оригинал не пропадает — он подсказкой стоит внутри пустого поля.
//
// Черновик правится локально и уезжает на сервер целиком по кнопке: страница
// тарифов публичная, и сохранять по каждому нажатию клавиши нельзя — на
// витрине оказался бы каждый промежуточный вариант.
import { useState, type ReactElement, type ReactNode } from 'react';
import { LANGS, LANG_META, useT, type Lang, type TranslationKey } from '../i18n';
import { ConfirmDialog } from '../ui/forms';
import {
  EDITABLE_TEXTS,
  FALLBACK_LANG,
  builtinText,
  type Limit,
  type LocalizedText,
  type PlanId,
} from '../pricing';
import { usePricingDoc } from './usePricingDoc';
import styles from './PricingPanel.module.css';

const PLANS: PlanId[] = ['free', 'pro'];
const LIMIT_KINDS: Limit['kind'][] = ['count', 'unlimited', 'yes', 'none', 'text'];

/** Тексты, которым на странице отведён абзац, а не строка: им нужен textarea. */
const LONG_TEXTS = new Set<TranslationKey>([
  'pricing.subtitle',
  'pricing.cta.note',
  'pricing.extras.hint',
  'pricing.soon.hint',
]);

/** Меняет элемент массива по индексу, не трогая остальные. */
function replaceAt<T>(list: T[], index: number, next: T): T[] {
  return list.map((item, i) => (i === index ? next : item));
}

/** Переставляет элемент на соседнюю позицию; за краями массива — без изменений. */
function moveAt<T>(list: T[], index: number, delta: number): T[] {
  const to = index + delta;
  if (to < 0 || to >= list.length) return list;
  const next = [...list];
  const [item] = next.splice(index, 1);
  next.splice(to, 0, item);
  return next;
}

/**
 * Свободный id для новой строки: `custom-1`, `custom-2`, …
 * Занятые проверяются по всему документу, потому что сервер требует
 * уникальности внутри каждого списка, а человек этого не видит.
 */
function freeId(taken: Set<string>, prefix = 'custom'): string {
  for (let i = 1; ; i += 1) {
    const candidate = `${prefix}-${i}`;
    if (!taken.has(candidate)) return candidate;
  }
}

// ── Поля ────────────────────────────────────────────────────────────────────

function NumberField({
  label,
  value,
  min = 0,
  step = 1,
  onChange,
}: {
  label: string;
  value: number;
  min?: number;
  step?: number;
  onChange(next: number): void;
}): ReactElement {
  return (
    <label className={styles.field}>
      <span className={styles.fieldLabel}>{label}</span>
      <input
        className="input"
        type="number"
        min={min}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
      />
    </label>
  );
}

/** Редактор одного значения лимита: вид, число и единица измерения. */
function LimitEditor({
  label,
  limit,
  onChange,
}: {
  label: string;
  limit: Limit;
  onChange(next: Limit): void;
}): ReactElement {
  const t = useT();

  function setKind(kind: Limit['kind']): void {
    if (kind === 'count') onChange({ kind, value: 1, per: 'month' });
    else if (kind === 'text') onChange({ kind, key: '' as TranslationKey });
    else onChange({ kind });
  }

  return (
    <div className={styles.limit}>
      <span className={styles.fieldLabel}>{label}</span>
      <div className={styles.limitRow}>
        <select
          className="select"
          value={limit.kind}
          aria-label={label}
          onChange={(e) => setKind(e.target.value as Limit['kind'])}
        >
          {LIMIT_KINDS.map((kind) => (
            <option key={kind} value={kind}>
              {t(`admin.pricing.kind.${kind}`)}
            </option>
          ))}
        </select>

        {limit.kind === 'count' && (
          <>
            <input
              className={`input ${styles.limitNumber}`}
              type="number"
              min={0}
              step={1}
              value={limit.value}
              aria-label={t('admin.pricing.kind.count')}
              onChange={(e) => onChange({ ...limit, value: Number(e.target.value) })}
            />
            <select
              className="select"
              value={limit.per}
              aria-label={t('admin.pricing.per.month')}
              onChange={(e) => onChange({ ...limit, per: e.target.value as 'total' | 'month' })}
            >
              <option value="total">{t('admin.pricing.per.total')}</option>
              <option value="month">{t('admin.pricing.per.month')}</option>
            </select>
          </>
        )}

        {limit.kind === 'text' && (
          <input
            className="input"
            value={limit.key}
            placeholder={t('admin.pricing.key')}
            aria-label={t('admin.pricing.key')}
            onChange={(e) => onChange({ kind: 'text', key: e.target.value as TranslationKey })}
          />
        )}
      </div>
    </div>
  );
}

/** Кнопки строки списка: выше, ниже, удалить. */
function RowTools({
  index,
  total,
  onMove,
  onRemove,
}: {
  index: number;
  total: number;
  onMove(delta: number): void;
  onRemove(): void;
}): ReactElement {
  const t = useT();
  return (
    <div className={styles.tools}>
      <button
        type="button"
        className="btn btn-sm"
        disabled={index === 0}
        title={t('admin.pricing.up')}
        aria-label={t('admin.pricing.up')}
        onClick={() => onMove(-1)}
      >
        ↑
      </button>
      <button
        type="button"
        className="btn btn-sm"
        disabled={index === total - 1}
        title={t('admin.pricing.down')}
        aria-label={t('admin.pricing.down')}
        onClick={() => onMove(1)}
      >
        ↓
      </button>
      <button
        type="button"
        className="btn btn-sm btn-danger"
        title={t('admin.pricing.remove')}
        aria-label={t('admin.pricing.remove')}
        onClick={onRemove}
      >
        ×
      </button>
    </div>
  );
}

function Block({ title, hint, children }: { title: string; hint?: string; children: ReactNode }): ReactElement {
  return (
    <section className={styles.block}>
      <h3 className={styles.blockTitle}>{title}</h3>
      {hint && <p className={styles.muted}>{hint}</p>}
      {children}
    </section>
  );
}

// ── Панель ──────────────────────────────────────────────────────────────────

export function PricingPanel(): ReactElement {
  const t = useT();
  const { doc, status, error, saved, updatedAt, updatedBy, patch, save, reset } = usePricingDoc();
  const [confirmReset, setConfirmReset] = useState(false);

  // Начинаем с оригинала: цены и устройство страницы правят на нём, а перевод
  // — работа, за которую садятся отдельно и осознанно переключившись.
  const [lang, setLang] = useState<Lang>(FALLBACK_LANG);
  const translating = lang !== FALLBACK_LANG;

  if (status === 'loading' || !doc) {
    return (
      <div className={styles.panel}>
        <p className={error ? styles.error : styles.muted}>{error || t('auth.busy')}</p>
      </div>
    );
  }

  const takenIds = new Set([
    ...doc.sections.map((s) => s.id),
    ...doc.features.map((f) => f.id),
    ...doc.soon.map((s) => s.id),
    ...doc.extras.map((e) => e.id),
    ...doc.faq.map((f) => f.id),
  ]);

  const full = doc.price.monthly * 12;
  const discount = full > 0 ? Math.round((1 - doc.price.yearly / full) * 100) : 0;

  const text = (key: TranslationKey): LocalizedText => doc.texts[key] ?? {};
  const setText = (key: TranslationKey, next: LocalizedText): void => {
    patch({ texts: { ...doc.texts, [key]: next } });
  };

  /**
   * Поле локализованного текста на выбранном сверху языке.
   *
   * `asKey` подписывает поле самим ключом словаря — так подписаны общие тексты
   * страницы, у которых человеческого названия нет. Ключ показывается как код,
   * а не как заголовок: иначе «PLAN.FREE.TAGLINE» читается как неудачный
   * перевод, а не как технический адрес строки. Что там окажется, видно по
   * подсказке внутри поля.
   */
  const field = (
    label: string,
    value: LocalizedText,
    key: TranslationKey | undefined,
    onChange: (next: LocalizedText) => void,
    long?: boolean,
    asKey?: boolean,
  ): ReactElement => {
    // Подсказка внутри поля — то, чем пустое поле обернётся на витрине. На
    // оригинале это встроенный текст, на переводе — сам оригинал: переводчику
    // нужен исходник перед глазами, иначе он переводит вслепую.
    const hint = translating
      ? value[FALLBACK_LANG] || builtinText(key, FALLBACK_LANG)
      : builtinText(key, lang);
    return (
      <label className={styles.field}>
        <span className={asKey ? styles.fieldKey : styles.fieldLabel}>{label}</span>
        {long ? (
          <textarea
            className="textarea"
            rows={3}
            value={value[lang] ?? ''}
            placeholder={hint}
            onChange={(e) => onChange({ ...value, [lang]: e.target.value })}
          />
        ) : (
          <input
            className="input"
            value={value[lang] ?? ''}
            placeholder={hint}
            onChange={(e) => onChange({ ...value, [lang]: e.target.value })}
          />
        )}
      </label>
    );
  };

  return (
    <div className={styles.panel}>
      <div className={styles.bar}>
        {/* Переключатель языка — первым в полосе: он меняет смысл всех полей
            ниже, и человек должен видеть его раньше, чем начнёт печатать. */}
        <label className={styles.langPick}>
          <span className={styles.fieldLabel}>{t('admin.pricing.lang')}</span>
          <select
            className="select"
            value={lang}
            onChange={(e) => setLang(e.target.value as Lang)}
          >
            {LANGS.map((code) => (
              <option key={code} value={code}>
                {LANG_META[code].flag} {LANG_META[code].name}
              </option>
            ))}
          </select>
        </label>

        <span className={styles.spacer} />

        <button
          type="button"
          className="btn"
          disabled={status === 'saving'}
          onClick={() => setConfirmReset(true)}
        >
          {t('admin.pricing.reset')}
        </button>
        <button
          type="button"
          className="btn btn-primary"
          disabled={status === 'saving'}
          onClick={() => void save()}
        >
          {t('admin.pricing.save')}
        </button>
      </div>

      <p className={styles.muted}>{t('admin.pricing.source')}</p>

      {error && <p className={styles.error}>{error}</p>}
      {saved && !error && <p className={styles.ok}>{t('admin.pricing.saved')}</p>}
      <p className={styles.muted}>
        {updatedAt
          ? t('admin.pricing.updated', {
              date: new Date(updatedAt).toLocaleString(),
              name: updatedBy || '—',
            })
          : t('admin.pricing.never')}
      </p>

      {/* ── Цены ──────────────────────────────────────────────────────── */}
      <Block title={t('admin.pricing.section.price')}>
        <div className={styles.grid3}>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>{t('admin.pricing.currency')}</span>
            <input
              className="input"
              value={doc.price.currency}
              maxLength={8}
              onChange={(e) => patch({ price: { ...doc.price, currency: e.target.value } })}
            />
          </label>
          <NumberField
            label={t('admin.pricing.monthly')}
            value={doc.price.monthly}
            onChange={(monthly) => patch({ price: { ...doc.price, monthly } })}
          />
          <NumberField
            label={t('admin.pricing.yearly')}
            value={doc.price.yearly}
            onChange={(yearly) => patch({ price: { ...doc.price, yearly } })}
          />
        </div>
        {/* Скидка считается тут же: цифра «48» сама по себе не отвечает на
            вопрос, выгоднее ли год, а на странице этот процент показан. */}
        <p className={discount > 0 ? styles.ok : styles.warn}>
          {discount > 0
            ? t('admin.pricing.discount', { percent: discount })
            : t('admin.pricing.discount.none')}
        </p>
      </Block>

      {/* ── Тексты страницы ───────────────────────────────────────────── */}
      <Block title={t('admin.pricing.section.texts')}>
        <div className={styles.grid2}>
          {EDITABLE_TEXTS.map((key) => (
            <div key={key}>
              {field(
                key.replace('pricing.', ''),
                text(key),
                key,
                (next) => setText(key, next),
                LONG_TEXTS.has(key),
                true,
              )}
            </div>
          ))}
        </div>
      </Block>

      {/* ── Разделы таблицы ───────────────────────────────────────────── */}
      <Block title={t('admin.pricing.section.sections')}>
        {doc.sections.map((section, i) => (
          <div key={section.id} className={styles.row}>
            <div className={styles.rowHead}>
              <code className={styles.id}>{section.id}</code>
              <RowTools
                index={i}
                total={doc.sections.length}
                onMove={(d) => patch({ sections: moveAt(doc.sections, i, d) })}
                onRemove={() =>
                  patch({
                    sections: doc.sections.filter((_, k) => k !== i),
                    // Строки осиротевшего раздела ушли бы со страницы молча и
                    // всплыли бы отказом сервера при сохранении — убираем сразу.
                    features: doc.features.filter((f) => f.section !== section.id),
                  })
                }
              />
            </div>
            {field(t('admin.pricing.name'), section.title, section.titleKey, (title) =>
              patch({ sections: replaceAt(doc.sections, i, { ...section, title }) }),
            )}
            {field(
              t('admin.pricing.hint'),
              section.hint,
              section.hintKey,
              (hint) => patch({ sections: replaceAt(doc.sections, i, { ...section, hint }) }),
              true,
            )}
          </div>
        ))}
        <button
          type="button"
          className="btn"
          onClick={() => {
            const id = freeId(takenIds, 'section');
            patch({ sections: [...doc.sections, { id, title: {}, hint: {} }] });
          }}
        >
          {t('admin.pricing.add')}
        </button>
      </Block>

      {/* ── Строки тарифов ────────────────────────────────────────────── */}
      <Block title={t('admin.pricing.section.features')}>
        {doc.features.length === 0 && <p className={styles.muted}>{t('admin.pricing.empty')}</p>}
        {doc.features.map((feature, i) => (
          <div key={feature.id} className={`${styles.row} ${feature.on ? '' : styles.rowOff}`}>
            <div className={styles.rowHead}>
              <code className={styles.id}>{feature.id}</code>
              <select
                className="select"
                value={feature.section}
                aria-label={t('admin.pricing.parent')}
                onChange={(e) =>
                  patch({
                    features: replaceAt(doc.features, i, { ...feature, section: e.target.value }),
                  })
                }
              >
                {doc.sections.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.title[lang] || builtinText(s.titleKey, lang) || s.id}
                  </option>
                ))}
              </select>
              <label className={styles.check}>
                <input
                  type="checkbox"
                  checked={feature.on}
                  onChange={(e) =>
                    patch({
                      features: replaceAt(doc.features, i, { ...feature, on: e.target.checked }),
                    })
                  }
                />
                {t('admin.pricing.shown')}
              </label>
              <RowTools
                index={i}
                total={doc.features.length}
                onMove={(d) => patch({ features: moveAt(doc.features, i, d) })}
                onRemove={() =>
                  patch({
                    features: doc.features.filter((_, k) => k !== i),
                    highlights: {
                      free: doc.highlights.free.filter((id) => id !== feature.id),
                      pro: doc.highlights.pro.filter((id) => id !== feature.id),
                    },
                  })
                }
              />
            </div>
            {field(t('admin.pricing.name'), feature.title, feature.titleKey, (title) =>
              patch({ features: replaceAt(doc.features, i, { ...feature, title }) }),
            )}
            <div className={styles.grid2}>
              <LimitEditor
                label={t('admin.pricing.free')}
                limit={feature.free}
                onChange={(free) =>
                  patch({ features: replaceAt(doc.features, i, { ...feature, free }) })
                }
              />
              <LimitEditor
                label={t('admin.pricing.pro')}
                limit={feature.pro}
                onChange={(pro) =>
                  patch({ features: replaceAt(doc.features, i, { ...feature, pro }) })
                }
              />
            </div>
          </div>
        ))}
        <button
          type="button"
          className="btn"
          disabled={doc.sections.length === 0}
          onClick={() => {
            const id = freeId(takenIds, 'row');
            patch({
              features: [
                ...doc.features,
                {
                  id,
                  section: doc.sections[0].id,
                  on: true,
                  title: {},
                  free: { kind: 'none' },
                  pro: { kind: 'yes' },
                },
              ],
            });
          }}
        >
          {t('admin.pricing.add')}
        </button>
      </Block>

      {/* ── Карточки тарифов ──────────────────────────────────────────── */}
      <Block title={t('admin.pricing.section.highlights')} hint={t('admin.pricing.highlights.hint')}>
        <div className={styles.grid2}>
          {PLANS.map((plan) => (
            <div key={plan}>
              <span className={styles.fieldLabel}>{t(`admin.pricing.${plan}`)}</span>
              <ul className={styles.picks}>
                {doc.features.map((feature) => (
                  <li key={feature.id}>
                    <label className={styles.check}>
                      <input
                        type="checkbox"
                        checked={doc.highlights[plan].includes(feature.id)}
                        onChange={(e) =>
                          patch({
                            highlights: {
                              ...doc.highlights,
                              [plan]: e.target.checked
                                ? [...doc.highlights[plan], feature.id]
                                : doc.highlights[plan].filter((id) => id !== feature.id),
                            },
                          })
                        }
                      />
                      {feature.title[lang] || builtinText(feature.titleKey, lang) || feature.id}
                    </label>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </Block>

      {/* ── Разовые покупки ───────────────────────────────────────────── */}
      <Block title={t('admin.pricing.section.extras')}>
        {doc.extras.map((extra, i) => (
          <div key={extra.id} className={styles.row}>
            <div className={styles.rowHead}>
              <code className={styles.id}>{extra.id}</code>
              <RowTools
                index={i}
                total={doc.extras.length}
                onMove={(d) => patch({ extras: moveAt(doc.extras, i, d) })}
                onRemove={() => patch({ extras: doc.extras.filter((_, k) => k !== i) })}
              />
            </div>
            {field(t('admin.pricing.name'), extra.title, extra.titleKey, (title) =>
              patch({ extras: replaceAt(doc.extras, i, { ...extra, title }) }),
            )}
            {field(
              t('admin.pricing.hint'),
              extra.hint,
              extra.hintKey,
              (hint) => patch({ extras: replaceAt(doc.extras, i, { ...extra, hint }) }),
              true,
            )}
            <NumberField
              label={t('admin.pricing.price')}
              value={extra.price}
              step={0.5}
              onChange={(price) => patch({ extras: replaceAt(doc.extras, i, { ...extra, price }) })}
            />
          </div>
        ))}
        <button
          type="button"
          className="btn"
          onClick={() => {
            const id = freeId(takenIds, 'extra');
            patch({ extras: [...doc.extras, { id, price: 0, title: {}, hint: {} }] });
          }}
        >
          {t('admin.pricing.add')}
        </button>
      </Block>

      {/* ── В работе ──────────────────────────────────────────────────── */}
      <Block title={t('admin.pricing.section.soon')}>
        {doc.soon.map((entry, i) => (
          <div key={entry.id} className={styles.row}>
            <div className={styles.rowHead}>
              <code className={styles.id}>{entry.id}</code>
              <RowTools
                index={i}
                total={doc.soon.length}
                onMove={(d) => patch({ soon: moveAt(doc.soon, i, d) })}
                onRemove={() => patch({ soon: doc.soon.filter((_, k) => k !== i) })}
              />
            </div>
            {field(t('admin.pricing.name'), entry.title, entry.titleKey, (title) =>
              patch({ soon: replaceAt(doc.soon, i, { ...entry, title }) }),
            )}
          </div>
        ))}
        <button
          type="button"
          className="btn"
          onClick={() => {
            const id = freeId(takenIds, 'soon');
            patch({ soon: [...doc.soon, { id, title: {} }] });
          }}
        >
          {t('admin.pricing.add')}
        </button>
      </Block>

      {/* ── Вопросы ───────────────────────────────────────────────────── */}
      <Block title={t('admin.pricing.section.faq')}>
        {doc.faq.map((entry, i) => (
          <div key={entry.id} className={styles.row}>
            <div className={styles.rowHead}>
              <code className={styles.id}>{entry.id}</code>
              <RowTools
                index={i}
                total={doc.faq.length}
                onMove={(d) => patch({ faq: moveAt(doc.faq, i, d) })}
                onRemove={() => patch({ faq: doc.faq.filter((_, k) => k !== i) })}
              />
            </div>
            {field(t('admin.pricing.question'), entry.question, entry.questionKey, (question) =>
              patch({ faq: replaceAt(doc.faq, i, { ...entry, question }) }),
            )}
            {field(
              t('admin.pricing.answer'),
              entry.answer,
              entry.answerKey,
              (answer) => patch({ faq: replaceAt(doc.faq, i, { ...entry, answer }) }),
              true,
            )}
          </div>
        ))}
        <button
          type="button"
          className="btn"
          onClick={() => {
            const id = freeId(takenIds, 'faq');
            patch({ faq: [...doc.faq, { id, question: {}, answer: {} }] });
          }}
        >
          {t('admin.pricing.add')}
        </button>
      </Block>

      <ConfirmDialog
        open={confirmReset}
        title={t('admin.pricing.reset.title')}
        message={t('admin.pricing.reset.text')}
        confirmLabel={t('admin.pricing.reset')}
        danger
        onCancel={() => setConfirmReset(false)}
        onConfirm={() => {
          setConfirmReset(false);
          void reset();
        }}
      />
    </div>
  );
}
